export const ITEM_IDS = [
  'beer',
  'hotdog',
  'porkRinds',
  'sushi',
  'dewd',
  'scratcher',
  'fireworks',
  'cans',
  'scrap',
  'parts',
  'catfish',
  'copper',
  'shine',
  'mash',
  'cigs',
  'wine',
  'whiskey',
  'scratchCow',
  'scratchGator',
  'scratchMega',
  'tvDinner',
  'pizza',
  'chicken',
  'bigEnergy',
  'rocketFuel',
  'flashlight',
  'headlamp',
  'foilHat',
  'alienGizmo',
  'rascal',
] as const;
export type ItemId = (typeof ITEM_IDS)[number];
export type Inventory = Record<ItemId, number>;

export const STAT_IDS = ['health', 'hunger', 'energy', 'buzz'] as const;
export type StatId = (typeof STAT_IDS)[number];
export type Stats = Record<StatId, number>;

// Every style a character can wear. The lists double as whitelists for saves and peers.
export const BODY_TYPES = ['man', 'woman'] as const;
export const HAIR_STYLES = [
  'mullet',
  'rattail',
  'teased',
  'bald',
  'buzz',
  'long',
  'ponytail',
  'bun',
] as const;
export const BEARD_STYLES = ['none', 'stache', 'goatee', 'full'] as const;
export const TOP_STYLES = [
  'tank',
  'tee',
  'flannel',
  'shirtless',
  'tracksuit',
  'camo',
  'nascar',
  'leopard',
  'tube',
] as const;
export const BOTTOM_STYLES = ['jorts', 'jeans', 'sweats', 'overalls', 'skirt'] as const;
export const SHOE_STYLES = ['crocs', 'boots', 'sneakers', 'slippers'] as const;
export const HAT_STYLES = ['none', 'trucker', 'backwards', 'cowboy'] as const;

export type BodyType = (typeof BODY_TYPES)[number];
export type HairStyle = (typeof HAIR_STYLES)[number];
export type BeardStyle = (typeof BEARD_STYLES)[number];
export type TopStyle = (typeof TOP_STYLES)[number];
export type BottomStyle = (typeof BOTTOM_STYLES)[number];
export type ShoeStyle = (typeof SHOE_STYLES)[number];
export type HatStyle = (typeof HAT_STYLES)[number];

/** Everything needed to build a character model. Shared by the player and NPCs. */
export interface CharacterLook {
  /** A man's build or a woman's (older saves and peers without one are men). */
  body?: BodyType;
  skin: string;
  hair: HairStyle;
  hairColor: string;
  beard: BeardStyle;
  top: TopStyle;
  topColor: string;
  bottom: BottomStyle;
  bottomColor: string;
  shoes: ShoeStyle;
  shoeColor: string;
  hat: HatStyle;
  hatColor: string;
  belly: number;
  height: number;
  shades?: boolean;
}

export type CounterId =
  | 'beers'
  | 'cansRecycled'
  | 'scrapSold'
  | 'scratchers'
  | 'scratchWins'
  | 'bigWins'
  | 'stunts'
  | 'stuntFails'
  | 'fireworks'
  | 'daytimeFireworks'
  | 'jobs'
  | 'meals'
  | 'sushi'
  | 'tornadoes'
  | 'passOuts'
  | 'pawned'
  | 'loans'
  | 'tv'
  | 'murray'
  | 'rentPaid'
  | 'sprints'
  | 'posts'
  | 'dumpsterDives'
  | 'talks'
  | 'noSnitch'
  | 'mlm'
  | 'camaroWork'
  | 'drives'
  | 'donuts'
  | 'swims'
  | 'erVisits'
  | 'noodles'
  | 'catfish'
  | 'catfishSold'
  | 'shineJars'
  | 'shineSold'
  | 'shineDrunk'
  | 'stillBooms'
  | 'copperLbs'
  | 'copperSold'
  | 'cornholeGames'
  | 'cornholeWins'
  | 'lot13'
  | 'trafficHits'
  | 'semiHits'
  | 'smokes'
  | 'mudRuns'
  | 'bullRides'
  | 'bullBest'
  | 'shootScore'
  | 'armWins'
  | 'dartsWins'
  | 'pokerWins'
  | 'lottoTickets'
  | 'lottoWins'
  | 'barTabs'
  | 'catfishBest'
  | 'duelWins'
  | 'laundry'
  | 'lostFound'
  | 'dryerCoins'
  | 'haircuts'
  | 'readings'
  | 'abductions'
  | 'couchFires'
  | 'campfires'
  | 'slushies'
  | 'favors'
  | 'carWashes'
  | 'deliveries'
  | 'shineRuns'
  | 'busted'
  | 'rentCollected'
  | 'flips';

export interface MemeImage {
  icon: string;
  top: string;
  bottom: string;
  bg: string;
}

export interface FeedPost {
  id: number;
  author: string;
  /** Avatar key: an NPC id, 'player', or a page id. */
  avatar: string;
  text: string;
  meme?: MemeImage;
  likes: number;
  liked?: boolean;
  day: number;
  minutes: number;
}

export type JobKind = 'mow' | 'haul';

export interface ActiveJob {
  id: string;
  kind: JobKind;
  title: string;
  pay: number;
  /** Absolute game minute the job expires. */
  deadline: number;
  /** Location key the job happens at (see world spots). */
  site: string;
  /** For haul jobs: the junk being hauled. */
  cargo?: string;
  stage: 'start' | 'active' | 'deliver';
  progress: number;
}

export type GameStatus = 'playing' | 'evicted' | 'won';

export interface InteriorState {
  items: string[];
  /** Owned, but put away in the closet. */
  stored: string[];
  walls: string;
  floor: string;
}

export interface GameState {
  version: number;
  seed: number;
  player: { name: string; look: CharacterLook };
  money: number;
  debt: number;
  clout: number;
  stats: Stats;
  inv: Inventory;
  time: { day: number; minutes: number };
  rent: { paidThroughWeek: number; strikes: number };
  decor: string[];
  /** What's inside your trailer: items you own, the ones in storage, and the paint and carpet. */
  interior: InteriorState;
  /** What you've put into the fund to buy Lucky Acres back from Apex Capital. */
  fund: number;
  /** Vehicles you bought at Cooter's (the Camaro is always yours), and the one in your driveway. */
  rides: string[];
  ride: string;
  /** Mega Bucks tickets for the drawing at the end of `week`. */
  lotto: { week: number; tickets: number[][] };
  heirlooms: string[];
  camaro: number;
  achievements: string[];
  counters: Partial<Record<CounterId, number>>;
  flags: Record<string, number | boolean | string>;
  feed: FeedPost[];
  nextPostId: number;
  job: ActiveJob | null;
  status: GameStatus;
}
